import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '@/lib/api';
import { SITE_ID } from '@/config/site';
import { trackAddedToCart } from '@/lib/integrations/storefront-events';

export interface CartItem {
  id: string;
  variant_id: string;
  product_id: string;
  product_name: string;
  variant_name: string;
  image_url: string | null;
  price: number;
  quantity: number;
  max_quantity: number;
  bundle_id: string | null;
  bundle_name: string | null;
  bundle_discount: number;
}

/** What every cart response carries back for the coupon (server re-evaluates on each change). */
interface CouponFields {
  coupon_code?: string | null;
  coupon_discount?: number;
  free_shipping?: boolean;
  coupon_removed_reason?: string;
}

interface CartState {
  items: CartItem[];
  couponCode: string | null;
  couponDiscount: number;
  /** The coupon zeroes shipping at checkout. */
  freeShipping: boolean;
  /** Set when the server dropped the code after a cart change (e.g. under its minimum). */
  couponRemovedReason: string | null;
  isLoading: boolean;

  fetchCart: () => Promise<void>;
  addItem: (variantId: string, quantity?: number) => Promise<void>;
  addBundle: (bundleId: string, variantSelections?: { product_id: string; variant_id: string }[]) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  applyCoupon: (code: string) => Promise<void>;
  removeCoupon: () => Promise<void>;
  clearCart: () => void;
  mergeGuestCart: () => Promise<void>;

  subtotal: () => number;
  bundleDiscount: () => number;
  total: () => number;
  itemCount: () => number;
}

/** Coupon slice from a cart response; absent fields leave the current values alone. */
function couponFrom(data: CouponFields): Partial<Pick<CartState, 'couponCode' | 'couponDiscount' | 'freeShipping' | 'couponRemovedReason'>> {
  if (!('coupon_code' in data) && !('coupon_discount' in data)) return {};
  return {
    couponCode: data.coupon_code ?? null,
    couponDiscount: data.coupon_discount ?? 0,
    freeShipping: data.free_shipping ?? false,
    couponRemovedReason: data.coupon_removed_reason ?? null,
  };
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      couponCode: null,
      couponDiscount: 0,
      freeShipping: false,
      couponRemovedReason: null,
      isLoading: false,

      fetchCart: async () => {
        set({ isLoading: true });
        try {
          const data = await api.get<{ items: CartItem[] } & CouponFields>('/cart');
          set({ items: data.items, ...couponFrom(data), isLoading: false });
        } catch {
          set({ isLoading: false });
        }
      },

      // Every mutation response carries the coupon RE-EVALUATED against the
      // new items (server-side), so the discount tracks the cart instead of
      // staying frozen at whatever it was when the code was typed.
      addItem: async (variantId, quantity = 1) => {
        set({ isLoading: true });
        try {
          const data = await api.post<{ items: CartItem[] } & CouponFields>('/cart/items', { variant_id: variantId, quantity });
          set({ items: data.items, ...couponFrom(data), isLoading: false });
          const added = data.items.find((i) => i.variant_id === variantId);
          if (added) trackAddedToCart({ ...added, quantity }, data.items);
        } catch {
          set({ isLoading: false });
        }
      },

      addBundle: async (bundleId, variantSelections) => {
        set({ isLoading: true });
        try {
          const body: any = { bundle_id: bundleId };
          if (variantSelections?.length) body.variant_selections = variantSelections;
          const data = await api.post<{ items: CartItem[] } & CouponFields>('/cart/bundle', body);
          set({ items: data.items, ...couponFrom(data), isLoading: false });
        } catch {
          set({ isLoading: false });
          throw new Error('Failed to add bundle');
        }
      },

      updateQuantity: async (itemId, quantity) => {
        set({ isLoading: true });
        try {
          const data = await api.put<{ items: CartItem[] } & CouponFields>(`/cart/items/${itemId}`, { quantity });
          set({ items: data.items, ...couponFrom(data), isLoading: false });
        } catch {
          set({ isLoading: false });
        }
      },

      removeItem: async (itemId) => {
        set({ isLoading: true });
        try {
          const data = await api.delete<{ items: CartItem[] } & CouponFields>(`/cart/items/${itemId}`);
          set({ items: data.items, ...couponFrom(data), isLoading: false });
        } catch {
          set({ isLoading: false });
        }
      },

      applyCoupon: async (code) => {
        const data = await api.post<{ coupon_code: string; coupon_discount: number; free_shipping?: boolean }>('/cart/coupon', { code });
        set({
          couponCode: data.coupon_code,
          couponDiscount: data.coupon_discount,
          freeShipping: data.free_shipping ?? false,
          couponRemovedReason: null,
        });
      },

      removeCoupon: async () => {
        await api.delete('/cart/coupon');
        set({ couponCode: null, couponDiscount: 0, freeShipping: false, couponRemovedReason: null });
      },

      clearCart: () => set({ items: [], couponCode: null, couponDiscount: 0, freeShipping: false, couponRemovedReason: null }),

      mergeGuestCart: async () => {
        const { items, couponCode } = get();
        if (items.length === 0 && !couponCode) return;
        try {
          for (const item of items) {
            await api.post('/cart/items', { variant_id: item.variant_id, quantity: item.quantity });
          }
          // Carry the guest's code over to the signed-in cart (it used to be
          // dropped on login); a code that doesn't apply is simply left off.
          if (couponCode) {
            await api.post('/cart/coupon', { code: couponCode }).catch(() => undefined);
          }
          await get().fetchCart();
        } catch {
          // Merge failed silently
        }
      },

      subtotal: () => get().items.reduce((sum, item) => sum + item.price * item.quantity, 0),
      bundleDiscount: () => get().items.reduce((sum, item) => sum + (item.bundle_discount || 0), 0),
      // Never below zero: the server caps a coupon at what it applies to, and
      // a stale persisted discount must not render a negative total.
      total: () => Math.max(0, get().subtotal() - get().couponDiscount - get().bundleDiscount()),
      itemCount: () => get().items.reduce((sum, item) => sum + item.quantity, 0),
    }),
    {
      name: `${SITE_ID}-cart`,
      partialize: (state) => ({ items: state.items, couponCode: state.couponCode, couponDiscount: state.couponDiscount }),
    }
  )
);
